import type { CourseDetailsData, CourseListApiItem } from "../types";

export type CompetencyPointRef = {
	uuid: string;
	name: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type CompetencyPointLike = {
	id?: number;
	uuid?: string;
	name?: string;
	description?: string | null;
};

function normalizeCompetencyPointUuid(value: unknown): string {
	if (typeof value === "string") {
		const trimmed = value.trim();
		return UUID_RE.test(trimmed) ? trimmed : "";
	}
	return "";
}

function readRecord(course: CourseListApiItem | CourseDetailsData): Record<string, unknown> {
	return course as Record<string, unknown>;
}

export function filterValidCompetencyPointUuids(ids: string[] | undefined | null): string[] {
	return (ids ?? [])
		.map(id => id.trim())
		.filter(id => UUID_RE.test(id));
}

export function collectCompetencyPointsFromCoursePayload(
	course?: CourseListApiItem | CourseDetailsData | null,
): CompetencyPointRef[] {
	if (!course) return [];

	const collected: CompetencyPointRef[] = [];
	const seen = new Set<string>();
	const nameByUuid = new Map<string, string>();

	const rememberName = (uuid: string, name?: string | null) => {
		const trimmed = name?.trim();
		if (trimmed && trimmed !== uuid) {
			nameByUuid.set(uuid, trimmed);
		}
	};

	const pushRef = (item?: CompetencyPointLike | null) => {
		const uuid = normalizeCompetencyPointUuid(item?.uuid);
		if (!uuid || seen.has(uuid)) return;
		rememberName(uuid, item?.name);
		seen.add(uuid);
		collected.push({
			uuid,
			name: item?.name?.trim() || nameByUuid.get(uuid) || uuid,
		});
	};

	const pushId = (rawId: unknown, name?: string | null) => {
		const uuid = normalizeCompetencyPointUuid(rawId);
		if (!uuid || seen.has(uuid)) return;
		rememberName(uuid, name);
		seen.add(uuid);
		collected.push({
			uuid,
			name: name?.trim() || nameByUuid.get(uuid) || uuid,
		});
	};

	const record = readRecord(course);

	// Primary BE shape: competencyPoints[] (replaces legacy competencyPoint object).
	const competencyPoints = (record.competencyPoints ?? record.competency_points) as
		| CompetencyPointLike[]
		| undefined;
	competencyPoints?.forEach(item => rememberName(normalizeCompetencyPointUuid(item?.uuid), item?.name));
	competencyPoints?.forEach(pushRef);

	const rawIds = record.competencyPointIds ?? record.competency_point_ids;
	if (Array.isArray(rawIds)) {
		rawIds.forEach(id => pushId(id, nameByUuid.get(normalizeCompetencyPointUuid(id))));
	}

	const rawCompetencyPointId = course.competencyPointId ?? record.competency_point_id;
	if (Array.isArray(rawCompetencyPointId)) {
		rawCompetencyPointId.forEach(id => {
			const uuid = normalizeCompetencyPointUuid(id);
			pushId(id, nameByUuid.get(uuid) ?? course.competencyPoint?.name);
		});
	} else {
		pushId(rawCompetencyPointId, course.competencyPoint?.name);
	}

	// Legacy single-object response (pre-multiselect).
	pushRef(course.competencyPoint);

	return collected;
}
